import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Upload, List, Tooltip, Select, Button, Popconfirm } from 'antd';
import { InboxOutlined, LeftOutlined, RightOutlined, FileImageOutlined, FilePdfOutlined, LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, ClearOutlined } from '@ant-design/icons';
import ImagesQuicklyCompress from 'images-quickly-compress';
import { PDFDocument } from 'pdf-lib';
import { imgOcr, getTaskResult } from '../../../../services/index';
import { models, languages, fileTypeItems } from '../../config';
import { saveFileToDB, getFileFromDB, deleteFileFromDB, clearFilesFromDB } from '../../../../utils/indexedDB';
import styles from './index.module.scss';

const { Dragger } = Upload;
const HISTORY_STORAGE_KEY = 'ocr_upload_history';
const CURRENT_TASK_STORAGE_KEY = 'ocr_current_task_id';

export const LeftPanel = ({ isCollapsed, onToggleCollapse }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const model = useSelector((state) => state.global.model);
  const language = useSelector((state) => state.global.language);
  const fileType = useSelector((state) => state.global.fileType);
  const inputValue = useSelector((state) => state.global.inputValue);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const latestPdfPreviewUrlRef = useRef(null);
  const currentTaskIdRef = useRef(null);
  const isProgrammaticChange = useRef(false);

  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem(HISTORY_STORAGE_KEY);
      const savedCurrentTaskId = localStorage.getItem(CURRENT_TASK_STORAGE_KEY);

      if (savedHistory) {
        const parsedHistory = JSON.parse(savedHistory);
        if (Array.isArray(parsedHistory)) {
          setUploadHistory(parsedHistory);

          const sessionId = localStorage.getItem('session_id') || '';
          if (sessionId) {
            parsedHistory.forEach((item) => {
              if (item?.status === 'processing' && item?.taskId) {
                pollTaskResult(item.taskId, sessionId, item.id);
              }
            });
          }
        }
      }

      if (savedCurrentTaskId) {
        setCurrentTaskId(Number(savedCurrentTaskId));
      }
    } catch (error) {
      localStorage.removeItem(HISTORY_STORAGE_KEY);
      localStorage.removeItem(CURRENT_TASK_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    try {
      const persistHistory = uploadHistory.slice(0, 50).map((item) => ({
        id: item.id,
        name: item.name,
        status: item.status,
        type: item.type,
        timestamp: item.timestamp,
        taskId: item.taskId || null,
        serverType: item.serverType || null,
        previewFileType: item.previewFileType || null,
        previewFileName: item.previewFileName || item.name,
        previewFileUrl: null,
        previewImage: null,
        result: item.result || null,
      }));

      let payload = JSON.stringify(persistHistory);
      if (payload.length > 900000) {
        payload = JSON.stringify(persistHistory.slice(0, 20));
      }
      localStorage.setItem(HISTORY_STORAGE_KEY, payload);
    } catch (error) {
      localStorage.removeItem(HISTORY_STORAGE_KEY);
    }
  }, [uploadHistory]);

  useEffect(() => {
    return () => {
      if (latestPdfPreviewUrlRef.current) {
        URL.revokeObjectURL(latestPdfPreviewUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (currentTaskId) {
      localStorage.setItem(CURRENT_TASK_STORAGE_KEY, String(currentTaskId));
    } else {
      localStorage.removeItem(CURRENT_TASK_STORAGE_KEY);
    }
  }, [currentTaskId]);

  useEffect(() => {
    currentTaskIdRef.current = currentTaskId;
  }, [currentTaskId]);

  // Sync user edits from RightPanel back to the current history item
  useEffect(() => {
    if (isProgrammaticChange.current) {
      isProgrammaticChange.current = false;
      return;
    }
    if (currentTaskId && inputValue != null) {
      updateHistoryItem(currentTaskId, { result: inputValue });
    }
  }, [inputValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function paste(e) {
      const clipdata = e.clipboardData || window.clipboardData;
      const files = clipdata.files;
      const imgFiles = [];
      if (files.length) {
        for (let i = 0; i < files.length; i++) {
          let FILE_MIME_REGEX = /^image\/(p?jpeg|gif|png)$/i;
          if (model === 'ultra') {
            FILE_MIME_REGEX = /^(image\/(p?jpeg|gif|png)|application\/pdf)$/i;
          }
          if (FILE_MIME_REGEX.test(files[i].type)) {
            imgFiles.push(files[i]);
          }
        }
      }
      if (imgFiles.length) {
        handleFileUpload({ file: imgFiles[0] });
        e.preventDefault();
      }
    }
    window.addEventListener('paste', paste);
    return () => {
      window.removeEventListener('paste', paste);
    };
  }, [model, language, fileType]);

  const imgQuality = async (file, targetSizeKb = 600) => {
    const imageCompress = new ImagesQuicklyCompress({
      size: `${targetSizeKb}kb`,
      imageType: file.type,
      quality: 0.82,
      orientation: true,
    });
    const files = await imageCompress.compressor([file]);
    return files?.[0] || file;
  };

  const loadImageElement = (file) => {
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(image);
      };
      image.onerror = (error) => {
        URL.revokeObjectURL(objectUrl);
        reject(error);
      };
      image.src = objectUrl;
    });
  };

  const resizeOversizedImage = async (file) => {
    const MAX_EDGE = 4096;
    const image = await loadImageElement(file);
    const originalWidth = image.naturalWidth || image.width;
    const originalHeight = image.naturalHeight || image.height;

    if (originalWidth <= MAX_EDGE && originalHeight <= MAX_EDGE) {
      return file;
    }

    const scale = Math.min(MAX_EDGE / originalWidth, MAX_EDGE / originalHeight);
    const targetWidth = Math.max(1, Math.round(originalWidth * scale));
    const targetHeight = Math.max(1, Math.round(originalHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      return file;
    }
    context.drawImage(image, 0, 0, targetWidth, targetHeight);

    const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await new Promise((resolve) => {
      canvas.toBlob(
        (resultBlob) => resolve(resultBlob),
        outputType,
        outputType === 'image/jpeg' ? 0.86 : undefined
      );
    });

    if (!blob) {
      return file;
    }

    const normalizedName = outputType === 'image/jpeg'
      ? file.name.replace(/\.[^.]+$/, '.jpg')
      : file.name;

    return new File([blob], normalizedName, {
      type: outputType,
      lastModified: Date.now(),
    });
  };

  const preprocessImageForUpload = async (file) => {
    try {
      const LARGE_IMAGE_SIZE = 5 * 1024 * 1024;
      const needsPreResize = file.size > LARGE_IMAGE_SIZE;
      const resized = needsPreResize ? await resizeOversizedImage(file) : file;
      const targetSizeKb = resized.size > LARGE_IMAGE_SIZE ? 900 : 600;
      return await imgQuality(resized, targetSizeKb);
    } catch (error) {
      console.error('Image preprocess error:', error);
      return file;
    }
  };

  const pdfSlice = (pdf, startPage = 0) => {
    return new Promise((resolve) => {
      const fileReader = new FileReader();
      fileReader.readAsArrayBuffer(pdf);
      fileReader.onload = async function () {
        try {
          const pdfDoc = await PDFDocument.load(new Uint8Array(this.result));
          const pageCount = pdfDoc.getPageCount();
          if (pageCount > 10) {
            const newPdfDoc = await PDFDocument.create();
            const indicesToCopy = [];
            for (let i = startPage; i < (startPage + 10 > pageCount ? pageCount : startPage + 10); i++) {
              indicesToCopy.push(i);
            }
            const copiedPages = await newPdfDoc.copyPages(pdfDoc, indicesToCopy);
            copiedPages.forEach((page) => {
              newPdfDoc.addPage(page);
            });
            const newFile = await newPdfDoc.save();
            const blob = new Blob([newFile], { type: pdf.type });
            const file = new File([blob], pdf.name, {
              type: pdf.type,
              lastModified: Date.now(),
            });
            resolve(file);
          } else {
            resolve(pdf);
          }
        } catch (error) {
          console.error('PDF processing error:', error);
          resolve(pdf);
        }
      };
    });
  };

  const handleFileUpload = async ({ file }) => {
    const sessionId = localStorage.getItem('session_id') || '';
    if (!sessionId) {
      dispatch({
        type: 'global/setShowLogin',
        payload: true,
      });
      return;
    }

    const historyItem = {
      id: Date.now(),
      name: file.name,
      status: 'processing',
      type: file.type,
      serverType: model,
      timestamp: new Date().toISOString(),
      previewImage: null,
      previewFileType: file.type === 'application/pdf' ? 'pdf' : 'image',
      previewFileName: file.name,
      previewFileUrl: null,
    };

    saveFileToDB(historyItem.id, file);

    setUploadHistory(prev => [historyItem, ...prev]);
    setCurrentTaskId(historyItem.id);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        updateHistoryItem(historyItem.id, {
          previewImage: e.target.result,
          previewFileUrl: e.target.result,
        });
        dispatch({
          type: 'global/setPreviewImage',
          payload: e.target.result,
        });
        dispatch({
          type: 'global/setPreviewFile',
          payload: {
            type: 'image',
            name: file.name,
            url: e.target.result,
          },
        });
      };
      reader.readAsDataURL(file);
    } else {
      if (latestPdfPreviewUrlRef.current) {
        URL.revokeObjectURL(latestPdfPreviewUrlRef.current);
      }
      const pdfPreviewUrl = URL.createObjectURL(file);
      latestPdfPreviewUrlRef.current = pdfPreviewUrl;
      updateHistoryItem(historyItem.id, {
        previewFileUrl: pdfPreviewUrl,
      });
      dispatch({
        type: 'global/setPreviewImage',
        payload: null,
      });
      dispatch({
        type: 'global/setPreviewFile',
        payload: {
          type: 'pdf',
          name: file.name,
          url: pdfPreviewUrl,
        },
      });
    }

    try {
      let processedFile;
      if (file.type === 'application/pdf') {
        processedFile = await pdfSlice(file, 0);
      } else {
        processedFile = await preprocessImageForUpload(file);
      }

      const formData = new FormData();
      formData.append('image', processedFile);
      formData.append('session_id', sessionId);
      formData.append('server_type', model);
      formData.append('language', language);
      formData.append('rec_config', JSON.stringify({ languages: [language] }));
      formData.append('file_type', fileType);

      const response = await imgOcr(formData);
      
      if (response.data && response.data.task_id) {
        updateHistoryItem(historyItem.id, { taskId: response.data.task_id });
        pollTaskResult(response.data.task_id, sessionId, historyItem.id);
      } else {
        updateHistoryItem(historyItem.id, { status: 'error' });
      }
    } catch (error) {
      updateHistoryItem(historyItem.id, { status: 'error' });
      console.error('Upload error:', error);
    }
  };

  const pollTaskResult = async (taskId, sessionId, historyId) => {
    const maxAttempts = 60;
    let attempts = 0;
    const FAST_INTERVAL = 500;
    const SLOW_INTERVAL = 2000;
    const FAST_PHASE_COUNT = 5;

    const poll = async () => {
      try {
        const result = await getTaskResult({
          task_id: taskId,
          session_id: sessionId,
        });

        const data = result?.data || {};
        const normalizedStatus = String(data.status || '').toUpperCase();
        const textResult = data.result || data.results;
        const isProcessing = ['PROGRESS', 'PROCESSING', 'PENDING', 'RUNNING', 'QUEUED'].includes(normalizedStatus);
        const isFailed = ['FAILED', 'ERROR'].includes(normalizedStatus);
        const isCompleted = (
          (data.status_code === 200 && !isProcessing) ||
          ['COMPLETED', 'SUCCESS', 'DONE', 'FINISHED'].includes(normalizedStatus)
        ) && !!textResult;

        if (isCompleted) {
          updateHistoryItem(historyId, {
            status: 'completed',
            result: textResult,
            taskId: taskId,
          });

          const isCurrentSelected = currentTaskIdRef.current === historyId;
          if (isCurrentSelected) {
            isProgrammaticChange.current = true;
            dispatch({
              type: 'global/setInputValue',
              payload: textResult,
            });
            dispatch({
              type: 'global/setTaskId',
              payload: taskId,
            });
          }
        } else if (isFailed) {
          updateHistoryItem(historyId, { status: 'error' });
        } else if (attempts < maxAttempts) {
          attempts++;
          const interval = attempts <= FAST_PHASE_COUNT ? FAST_INTERVAL : SLOW_INTERVAL;
          setTimeout(poll, interval);
        } else {
          updateHistoryItem(historyId, { status: 'error' });
        }
      } catch (error) {
        updateHistoryItem(historyId, { status: 'error' });
      }
    };

    poll();
  };

  const updateHistoryItem = (id, updates) => {
    setUploadHistory(prev => 
      prev.map(item => item.id === id ? { ...item, ...updates } : item)
    );
  };

  const clearCurrentPreviewState = () => {
    dispatch({ type: 'global/setPreviewImage', payload: null });
    dispatch({ type: 'global/setPreviewFile', payload: null });
    dispatch({ type: 'global/setTaskId', payload: '' });
  };

  const removeHistoryItem = (id) => {
    deleteFileFromDB(id);
    setUploadHistory((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target?.previewFileUrl && String(target.previewFileUrl).startsWith('blob:')) {
        URL.revokeObjectURL(target.previewFileUrl);
        if (latestPdfPreviewUrlRef.current === target.previewFileUrl) {
          latestPdfPreviewUrlRef.current = null;
        }
      }
      return prev.filter((item) => item.id !== id);
    });

    if (currentTaskId === id) {
      setCurrentTaskId(null);
      clearCurrentPreviewState();
    }
  };

  const clearAllHistory = () => {
    clearFilesFromDB();
    uploadHistory.forEach((item) => {
      if (item?.previewFileUrl && String(item.previewFileUrl).startsWith('blob:')) {
        URL.revokeObjectURL(item.previewFileUrl);
      }
    });
    latestPdfPreviewUrlRef.current = null;
    setUploadHistory([]);
    setCurrentTaskId(null);
    clearCurrentPreviewState();
  };

  const handleHistoryClick = async (item) => {
    isProgrammaticChange.current = true;
    setCurrentTaskId(item.id);

    // Restore file from IndexedDB if preview url is missing
    let finalPreviewUrl = item.previewFileUrl;
    let finalPreviewImage = item.previewImage;

    if (!finalPreviewUrl && !finalPreviewImage) {
      try {
        const fileBlob = await getFileFromDB(item.id);
        if (fileBlob) {
          finalPreviewUrl = URL.createObjectURL(fileBlob);
          finalPreviewImage = item.type.startsWith('image/') ? finalPreviewUrl : null;
          
          updateHistoryItem(item.id, {
            previewFileUrl: finalPreviewUrl,
            previewImage: finalPreviewImage,
          });
        }
      } catch (error) {
        console.error('Error restoring file from DB:', error);
      }
    }

    dispatch({
      type: 'global/setPreviewImage',
      payload: finalPreviewImage || null,
    });
    dispatch({
      type: 'global/setPreviewFile',
      payload: {
        type: item.previewFileType || (item.type === 'application/pdf' ? 'pdf' : 'image'),
        name: item.previewFileName || item.name,
        url: finalPreviewUrl || finalPreviewImage || null,
      },
    });

    if (item.status === 'completed' && item.result) {
      dispatch({
        type: 'global/setInputValue',
        payload: item.result,
      });
      dispatch({
        type: 'global/setTaskId',
        payload: item.taskId,
      });
      return;
    }

    if (item.status === 'completed' && item.taskId) {
      try {
        const sessionId = localStorage.getItem('session_id') || '';
        if (!sessionId) {
          return;
        }

        const result = await getTaskResult({
          task_id: item.taskId,
          session_id: sessionId,
        });

        const data = result?.data || {};
        const textResult = data.result || data.results;
        if (textResult) {
          updateHistoryItem(item.id, { result: textResult });
          dispatch({
            type: 'global/setInputValue',
            payload: textResult,
          });
          dispatch({
            type: 'global/setTaskId',
            payload: item.taskId,
          });
        }
      } catch (error) {
        // noop
      }
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
    case 'processing':
      return <LoadingOutlined spin style={{ color: '#1890ff' }} />;
    case 'completed':
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    case 'error':
      return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    default:
      return null;
    }
  };

  const getFileIcon = (type) => {
    if (type === 'application/pdf') {
      return <FilePdfOutlined style={{ fontSize: 20, color: '#ff4d4f' }} />;
    }
    return <FileImageOutlined style={{ fontSize: 20, color: '#1890ff' }} />;
  };

  return (
    <div className={`${styles.leftPanel} ${isCollapsed ? styles.collapsed : ''}`}>
      <div className={styles.collapseButton} onClick={onToggleCollapse}>
        {isCollapsed ? <RightOutlined /> : <LeftOutlined />}
      </div>

      {!isCollapsed && (
        <>
          <div className={styles.configSection}>
            <div className={styles.configItem}>
              <label>{t('leftPanel.model')}:</label>
              <Select
                value={model}
                onChange={(value) => dispatch({ type: 'global/setModel', payload: value })}
                options={models}
                size="small"
                style={{ flex: 1 }}
              />
            </div>
            <div className={styles.configItem}>
              <label>{t('leftPanel.language')}:</label>
              <Select
                value={language}
                onChange={(value) => dispatch({ type: 'global/setLanguage', payload: value })}
                options={languages}
                size="small"
                style={{ flex: 1 }}
              />
            </div>
            <div className={styles.configItem}>
              <label>{t('leftPanel.type')}:</label>
              <Select
                value={fileType}
                onChange={(value) => dispatch({ type: 'global/setFileType', payload: value })}
                options={fileTypeItems}
                size="small"
                style={{ flex: 1 }}
              />
            </div>
          </div>

          <div className={styles.uploadSection}>
            <h3 className={styles.sectionTitle}>{t('leftPanel.uploadFile')}</h3>
            <Dragger
              accept={model === 'ultra' ? 'image/*,application/pdf' : 'image/*'}
              beforeUpload={() => false}
              onChange={handleFileUpload}
              showUploadList={false}
              className={styles.dragger}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">{t('leftPanel.clickOrDrag')}</p>
              <p className="ant-upload-hint">
                {model === 'ultra' ? t('leftPanel.supportImagesPdf') : t('leftPanel.supportImages')}
              </p>
            </Dragger>
          </div>

          <div className={styles.historySection}>
            <div className={styles.historyHeader}>
              <h3 className={styles.sectionTitle}>{t('leftPanel.processingHistory')}</h3>
              <Popconfirm
                title={t('leftPanel.clearAllConfirm')}
                onConfirm={clearAllHistory}
                okText={t('common.confirm')}
                cancelText={t('common.cancel')}
                disabled={!uploadHistory.length}
              >
                <Button
                  size="small"
                  icon={<ClearOutlined />}
                  className={styles.clearAllButton}
                  disabled={!uploadHistory.length}
                >
                  {t('leftPanel.clearAll')}
                </Button>
              </Popconfirm>
            </div>
            <List
              className={styles.historyList}
              dataSource={uploadHistory}
              locale={{ emptyText: t('leftPanel.noHistory') }}
              renderItem={(item) => (
                <List.Item
                  className={`${styles.historyItem} ${item.id === currentTaskId ? styles.active : ''}`}
                  onClick={() => handleHistoryClick(item)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className={styles.historyItemContent}>
                    <div className={styles.fileInfo}>
                      {getFileIcon(item.type)}
                      <Tooltip title={item.name}>
                        <span className={styles.fileName}>{item.name}</span>
                      </Tooltip>
                    </div>
                    <div className={styles.historyActions}>
                      <Popconfirm
                        title={t('leftPanel.deleteItemConfirm')}
                        onConfirm={(e) => {
                          e?.stopPropagation?.();
                          removeHistoryItem(item.id);
                        }}
                        okText={t('common.confirm')}
                        cancelText={t('common.cancel')}
                        onPopupClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip title={t('leftPanel.deleteItem')}>
                          <Button
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            className={styles.deleteBtn}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Tooltip>
                      </Popconfirm>
                      <div className={styles.statusInfo}>
                        {getStatusIcon(item.status)}
                      </div>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </div>
        </>
      )}
    </div>
  );
};
